/**
 * 👥 Admin Management Page
 * Super admin interface for managing admin users and viewing activity logs
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Grid,
  Paper,
  Pagination,
  InputAdornment,
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Block,
  Delete,
  VpnKey,
  Refresh,
  Search,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  History,
} from '@mui/icons-material';
import { adminStaffService, AdminStaff, SystemRole, AuditLog } from '@/services/adminStaffService';
import { usePermissions } from '@/hooks/usePermissions';
import toast from 'react-hot-toast';
import { useHasChanges } from '@/hooks/useHasChanges';

export const AdminManagementPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { isSuperAdmin, can, Permissions } = usePermissions();

  // Check if user has permission to access this page
  const canManageAdmins = isSuperAdmin() || can('admin_staff', 'view');

  if (!canManageAdmins) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to manage admin users.</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
          Admin Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage admin users, roles, and permissions
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab icon={<AdminPanelSettings />} label="Admin Users" />
          <Tab icon={<History />} label="Activity Log" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && <AdminUsersTab />}
      {selectedTab === 1 && <ActivityLogTab />}
    </Box>
  );
};

/**
 * Admin Users Tab Component
 */
const AdminUsersTab: React.FC = () => {
  const [admins, setAdmins] = useState<AdminStaff[]>([]);
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminStaff | null>(null);

  const { can, isSuperAdmin, hasRole } = usePermissions();
  // Admin and super_admin can create/modify/delete admin staff
  const isAdminOrSuperAdmin = isSuperAdmin() || hasRole('admin') || hasRole('Admin');
  const canCreate = isAdminOrSuperAdmin || can('admin_staff', 'create');
  const canModify = isAdminOrSuperAdmin || can('admin_staff', 'modify');
  const canDelete = isAdminOrSuperAdmin || can('admin_staff', 'delete');

  useEffect(() => {
    loadAdmins();
    loadRoles();
  }, [page, searchQuery, statusFilter, roleFilter]);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const response = await adminStaffService.getAdminStaff({
        page,
        limit: 20,
        status: statusFilter === 'all' ? undefined : (statusFilter as any),
        role: roleFilter === 'all' ? undefined : roleFilter,
        search: searchQuery,
      });

      if (response.success && response.data) {

        // Backend returns role as a string, so normalize it to our expected format
        const normalizedAdmins = response.data.admins.map((admin: any) => ({
          ...admin,
          role: typeof admin.role === 'string'
            ? { id: admin.role, name: admin.role, type: 'system' as const }
            : admin.role,
          department: admin.department || 'N/A'
        }));

        setAdmins(normalizedAdmins);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        toast.error(response.error || 'Failed to load admin users');
      }
    } catch (error: any) {
      console.error('Failed to load admins:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await adminStaffService.getSystemRoles();

      if (response.success && response.data) {
        // Backend returns data as array directly, not data.roles
        const rolesArray = Array.isArray(response.data) ? response.data : response.data.roles || [];
        setRoles(rolesArray);
      } else {
        console.error('❌ Failed to load roles:', response.error);
        toast.error('Failed to load system roles');
      }
    } catch (error) {
      console.error('❌ Error loading roles:', error);
      toast.error('Failed to load system roles');
    }
  };

  const handleDelete = async (admin: AdminStaff) => {
    if (!canDelete) {
      toast.error('You do not have permission to delete admin users');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${admin.fullName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminStaffService.deleteAdminStaff(admin.id);

      if (response.success) {
        toast.success(response.message || 'Admin user deleted successfully');
        loadAdmins();
      } else {
        toast.error(response.error || 'Failed to delete admin user');
      }
    } catch (error) {
      console.error('Failed to delete admin:', error);
      toast.error('Failed to delete admin user');
    }
  };

  const handleSuspend = async (admin: AdminStaff) => {
    if (!canDelete) {
      toast.error('You do not have permission to suspend admin users');
      return;
    }

    if (!confirm(`Are you sure you want to suspend ${admin.fullName}?`)) {
      return;
    }

    try {
      const response = await adminStaffService.suspendAdminStaff(admin.id);

      if (response.success) {
        toast.success('Admin user suspended successfully');
        loadAdmins();
      } else {
        toast.error(response.error || 'Failed to suspend admin user');
      }
    } catch (error) {
      console.error('Failed to suspend admin:', error);
      toast.error('Failed to suspend admin user');
    }
  };

  const handleResendInvitation = async (admin: AdminStaff) => {
    if (!canModify) {
      toast.error('You do not have permission to resend invitations');
      return;
    }

    try {
      const response = await adminStaffService.resendInvitation(admin.id);

      if (response.success) {
        toast.success(response.message || `Invitation email sent to ${admin.email}`);
      } else {
        toast.error(response.error || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error('Failed to resend invitation');
    }
  };

  const handleResetPassword = async (admin: AdminStaff) => {
    if (!canModify) {
      toast.error('You do not have permission to reset passwords');
      return;
    }

    try {
      const response = await adminStaffService.resetPassword(admin.id, {
        sendEmail: true,
      });

      if (response.success) {
        toast.success('Password reset email sent successfully');
      } else {
        toast.error(response.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
                <MenuItem value="all">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadAdmins}
                fullWidth
              >
                Refresh
              </Button>
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setCreateDialogOpen(true)}
                  fullWidth
                >
                  Add Admin
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Admin Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No admin users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell>{admin.fullName}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      {admin.role?.name ? (
                        <Chip label={admin.role.name} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>{admin.department || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={admin.status}
                        size="small"
                        color={getStatusColor(admin.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {admin.lastLoginAt
                        ? new Date(admin.lastLoginAt).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {canModify && (
                          <>
                            {admin.status === 'pending_setup' ? (
                              <Tooltip title="Resend Invitation">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleResendInvitation(admin)}
                                >
                                  <Refresh />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedAdmin(admin);
                                      setEditDialogOpen(true);
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset Password">
                                  <IconButton size="small" onClick={() => handleResetPassword(admin)}>
                                    <VpnKey />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                        {canDelete && (
                          <>
                            {admin.status === 'active' && (
                              <Tooltip title="Suspend">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleSuspend(admin)}
                                >
                                  <Block />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(admin)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Create Admin Dialog */}
      <CreateAdminDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        roles={roles}
        onSuccess={loadAdmins}
      />

      {/* Edit Admin Dialog */}
      <EditAdminDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAdmin(null);
        }}
        admin={selectedAdmin}
        roles={roles}
        onSuccess={loadAdmins}
      />
    </Box>
  );
};

/**
 * Activity Log Tab Component
 */
const ActivityLogTab: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Action label mapping for human-readable names
  const actionLabels: Record<string, string> = {
    'auth:login': 'Logged In',
    'auth:logout': 'Logged Out',
    'auth:failed_login': 'Failed Login',
    'verifications:approve': 'Approved Verification',
    'verifications:reject': 'Rejected Verification',
    'business:approve': 'Approved Business',
    'business:reject': 'Rejected Business',
    'vehicles:approve': 'Approved Vehicle',
    'vehicles:reject': 'Rejected Vehicle',
    'users:suspend': 'Suspended User',
    'users:unsuspend': 'Unsuspended User',
    'admin:create': 'Created Admin',
    'admin:update': 'Updated Admin',
    'admin:delete': 'Deleted Admin',
    'admin:suspend': 'Suspended Admin',
    'admin:password_reset': 'Reset Password',
  };

  const getActionLabel = (action: string) => {
    return actionLabels[action] || action;
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'blocked':
        return 'warning';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    loadLogs();
  }, [page, actionFilter, statusFilter, startDate, endDate]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: any = {
        page,
        limit: 50,
      };

      if (actionFilter !== 'all') filters.action = actionFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (startDate) filters.startDate = new Date(startDate).toISOString();
      if (endDate) filters.endDate = new Date(endDate).toISOString();

      const response = await adminStaffService.getAuditLogs(filters);


      if (response.success && response.data) {
        setLogs(response.data.logs);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError('Audit logs endpoint not yet implemented. Contact backend team to enable this feature.');
        setLogs([]);
      }
    } catch (error: any) {
      console.error('Failed to load logs:', error);
      // Check if it's a 401/404 error
      if (error.response?.status === 401 || error.response?.status === 404) {
        setError('Audit logs endpoint not yet implemented. Contact backend team to enable this feature.');
      } else {
        setError('Failed to load audit logs. Please try again later.');
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDetails = (log: any) => {
    // For login actions, show N/A
    if (log.action === 'auth:login') {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }

    // Helper to format key-value pairs nicely
    const formatKeyValue = (obj: any, maxItems = 3) => {
      if (!obj || typeof obj !== 'object') return null;
      const entries = Object.entries(obj).filter(([key]) =>
        !['id', 'createdAt', 'updatedAt', 'adminUserId'].includes(key)
      );
      const displayEntries = entries.slice(0, maxItems);
      const hasMore = entries.length > maxItems;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {displayEntries.map(([key, value]) => {
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
            const capitalizedKey = formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
            let displayValue = value;

            if (typeof value === 'boolean') {
              displayValue = value ? 'Yes' : 'No';
            } else if (typeof value === 'object' && value !== null) {
              displayValue = Array.isArray(value) ? `${value.length} items` : 'Object';
            } else if (typeof value === 'string' && value.length > 30) {
              displayValue = `${value.substring(0, 30)}...`;
            }

            return (
              <Typography key={key} variant="caption" sx={{ lineHeight: 1.3 }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>{capitalizedKey}:</Box>{' '}
                <Box component="span" sx={{ fontWeight: 500 }}>{String(displayValue)}</Box>
              </Typography>
            );
          })}
          {hasMore && (
            <Typography variant="caption" color="text.secondary">
              +{entries.length - maxItems} more
            </Typography>
          )}
        </Box>
      );
    };

    // If we have oldValue and newValue, show the changes
    if (log.oldValue || log.newValue) {
      const oldVal = log.oldValue;
      const newVal = log.newValue;

      // If both are objects with a 'status' field, show just the status change
      if (oldVal?.status && newVal?.status) {
        return (
          <Typography variant="caption">
            <Box component="span" sx={{ color: 'text.secondary' }}>Status:</Box>{' '}
            <Box component="span" sx={{ textDecoration: 'line-through', color: 'error.main' }}>{oldVal.status}</Box>
            {' → '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>{newVal.status}</Box>
          </Typography>
        );
      }

      // Show formatted new value if available
      if (newVal && typeof newVal === 'object') {
        return formatKeyValue(newVal);
      }
    }

    // Show metadata if available
    if (log.metadata) {
      if (typeof log.metadata === 'object') {
        return formatKeyValue(log.metadata);
      }
      const metaStr = String(log.metadata);
      return (
        <Typography variant="caption" color="text.secondary">
          {metaStr.length > 50 ? `${metaStr.substring(0, 50)}...` : metaStr}
        </Typography>
      );
    }

    return <Typography variant="caption" color="text.secondary">—</Typography>;
  };

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={actionFilter}
                label="Action"
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="auth:login">Login</MenuItem>
                <MenuItem value="verifications:approve">Approve Verification</MenuItem>
                <MenuItem value="verifications:reject">Reject Verification</MenuItem>
                <MenuItem value="business:approve">Approve Business</MenuItem>
                <MenuItem value="business:reject">Reject Business</MenuItem>
                <MenuItem value="vehicles:approve">Approve Vehicle</MenuItem>
                <MenuItem value="vehicles:reject">Reject Vehicle</MenuItem>
                <MenuItem value="users:suspend">Suspend User</MenuItem>
                <MenuItem value="users:unsuspend">Unsuspend User</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadLogs}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Admin</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No activity logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log: any) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      {log.createdAt
                        ? (() => {
                            const date = new Date(log.createdAt);
                            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                          })()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {log.adminEmail?.split('@')[0] || 'System'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.adminEmail || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getActionLabel(log.action) || 'N/A'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.status || 'N/A'}
                        size="small"
                        color={getStatusColor(log.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.resourceType || '—'}
                      </Typography>
                      {log.resourceId && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {log.resourceId.substring(0, 8)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDetails(log)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

/**
 * Create Admin Dialog Component
 */
interface CreateAdminDialogProps {
  open: boolean;
  onClose: () => void;
  roles: SystemRole[];
  onSuccess: () => void;
}

const CreateAdminDialog: React.FC<CreateAdminDialogProps> = ({
  open,
  onClose,
  roles,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phone: '',
    roleId: '',
    department: '',
    password: '', // Required field now
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.email || !formData.fullName || !formData.roleId || !formData.password) {
      toast.error('Please fill in all required fields (Email, Name, Role, Password)');
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await adminStaffService.createAdminStaff({
        ...formData,
        generatePassword: false, // We're providing a password
      });

      if (response.success) {
        toast.success(
          'Admin staff created successfully. They can login now with the credentials you set.',
          { duration: 5000 }
        );
        onClose();
        onSuccess();
        // Reset form
        setFormData({
          email: '',
          fullName: '',
          phone: '',
          roleId: '',
          department: '',
          password: '',
        });
      } else {
        // Handle duplicate email error
        if (response.errorCode === 'ADMIN_ALREADY_EXISTS') {
          toast.error('An admin with this email already exists');
        } else {
          toast.error(response.error || 'Failed to create admin user');
        }
      }
    } catch (error) {
      console.error('Failed to create admin:', error);
      toast.error('Failed to create admin user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Admin User</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.roleId}
              label="Role"
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            fullWidth
            helperText="Password must be at least 8 characters. Staff member can login immediately with this password."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            fullWidth
          />
          <TextField
            label="Department"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAdd />}
        >
          {loading ? 'Creating...' : 'Create Admin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Admin Dialog Component
 */
interface EditAdminDialogProps {
  open: boolean;
  onClose: () => void;
  admin: AdminStaff | null;
  roles: SystemRole[];
  onSuccess: () => void;
}

const EditAdminDialog: React.FC<EditAdminDialogProps> = ({
  open,
  onClose,
  admin,
  roles,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    roleId: '',
    department: '',
    status: 'active' as 'active' | 'suspended' | 'inactive',
  });
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<typeof formData | null>(null);
  const hasChanges = useHasChanges(formData, initialData);

  useEffect(() => {
    if (admin) {
      const loaded = {
        fullName: admin.fullName,
        phone: admin.phone || '',
        roleId: typeof admin.role === 'string' ? admin.role : admin.role.id,
        department: admin.department || '',
        status: admin.status,
      };
      setFormData(loaded);
      setInitialData(loaded);
    }
  }, [admin]);

  const handleSubmit = async () => {
    if (!admin) return;

    setLoading(true);
    try {
      const response = await adminStaffService.updateAdminStaff(admin.id, formData);

      if (response.success) {
        toast.success('Admin user updated successfully');
        onClose();
        onSuccess();
      } else {
        toast.error(response.error || 'Failed to update admin user');
      }
    } catch (error) {
      console.error('Failed to update admin:', error);
      toast.error('Failed to update admin user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Admin User</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            fullWidth
          />
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.roleId}
              label="Role"
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Department"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !hasChanges}
          startIcon={loading ? <CircularProgress size={20} /> : <Edit />}
        >
          {loading ? 'Updating...' : 'Update Admin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminManagementPage;
